import {
  FormFields,
  FormValidations,
  SetValidationErrorFunction,
  SetValidationRuleFunction,
  ValidationActionType,
} from "features/shared/forms/hooks/useForm";
import PatternValidatonRule from "features/validation/rules/PatternValidatonRule";
import BankAccountValidator from "features/validation/validators/BankAccountValidator";
import { isValidIBAN } from "ibantools";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import _ from "lodash";

export const bankAccountValidations = (): FormValidations => {
  return {
    bankCode: {
      required: true,
      type: "OnlyRequired",
    },
    accountNumber: {
      required: true,
      regex: /^\d{2,10}$/,
      message: "bankAccountValidations.accountNumber",
      type: "Pattern",
    } as PatternValidatonRule,
    preAccountNumber: {
      regex: /^\d{1,6}$/,
      enableEmptyValue: true,
      message: "bankAccountValidations.preAccountNumber",
      type: "Pattern",
    } as PatternValidatonRule,
    iban: {
      required: true,
      regex: /^(CZ|SK).*$/,
      message: "bankAccountValidations.iban",
      type: "Pattern",
    } as PatternValidatonRule,
  };
};

export const validatePreAccountNumber = (
  preAccountNumber: string,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction
): boolean => {
  let accountValidator = inversifyContainer.get<BankAccountValidator>(
    IocTypes.BankAccountValidator
  );
  let isValid = false;

  if (validateFormField("preAccountNumber", preAccountNumber)) {
    if (!accountValidator.validatePreAccountNumber(preAccountNumber, false)) {
      setValidationError(
        "preAccountNumber",
        "bankAccountValidations.invalidPreAccountNumber"
      );
    } else {
      setValidationError("preAccountNumber", null, "remove");
      isValid = true;
    }
  }

  return isValid;
};

export const validateAccountNumber = (
  accountNumber: string,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction
): boolean => {
  let accountValidator = inversifyContainer.get<BankAccountValidator>(
    IocTypes.BankAccountValidator
  );
  let isValid = false;

  if (validateFormField("accountNumber", accountNumber)) {
    if (!accountValidator.validateAccountNumber(accountNumber, false)) {
      setValidationError(
        "accountNumber",
        "bankAccountValidations.invalidAccountNumber"
      );
    } else {
      setValidationError("accountNumber", null, "remove");
      isValid = true;
    }
  }

  return isValid;
};

export const validateBankAccountNumber = (
  values: FormFields,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction
): boolean => {
  let isValid1 = validatePreAccountNumber(
    _.get(values, "preAccountNumber"),
    validateFormField,
    setValidationError
  );
  let isValid2 = validateAccountNumber(
    _.get(values, "accountNumber"),
    validateFormField,
    setValidationError
  );
  let isValid3 = validateFormField("bankCode", _.get(values, "bankCode"));

  return isValid1 && isValid2 && isValid3;
};

export const validateIbanNumber = (
  iban: string,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction
): boolean => {
  let isValid = false;

  if (validateFormField("iban", iban)) {
    if (!isValidIBAN(iban)) {
      setValidationError("iban", "bankAccountValidations.invalidIban");
    } else {
      setValidationError("iban", null, "remove");
      isValid = true;
    }
  }

  return isValid;
};

export const setBankAccountValidations = (
  setValidationRule: SetValidationRuleFunction,
  action: ValidationActionType = "add"
) => {
  const validations = bankAccountValidations();

  setValidationRule("bankCode", validations["bankCode"], action);
  setValidationRule("accountNumber", validations["accountNumber"], action);
  setValidationRule(
    "preAccountNumber",
    validations["preAccountNumber"],
    action
  );
};
